import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { API, adminGetAiChatHistory } from '../../lib/api';
import { streamChat, getOrCreateSession } from '../../lib/aiStream';
import { Bot, Send, RotateCcw, Sparkles, Zap } from 'lucide-react';

const SUGGESTIONS = [
  'How is the business doing overall?',
  'Any unpaid orders I should chase?',
  'What should I reorder first and why?',
  'Who are my top customers?',
];

const TOOL_LABELS = {
  get_orders: 'Checking live orders',
  get_order_details: 'Pulling order details',
  get_stock_levels: 'Checking stock levels',
  get_finances: 'Crunching the finances',
  get_customers: 'Looking up customers',
  get_abandoned_checkouts: 'Checking abandoned carts',
  update_order: 'Updating the order',
  send_abandoned_cart_code: 'Sending recovery code',
  bulk_cancel_orders: 'Cancelling orders',
  remember: 'Saving a note',
  forget: 'Removing a note',
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 120, damping: 18 } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

export default function AdminAssistant() {
  const [sessionId, setSessionId] = useState(() => getOrCreateSession('alp_admin_ai_session'));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    adminGetAiChatHistory(sessionId).then((h) => {
      setMessages((m) => (m.length ? m : h));
      try {
        const pending = localStorage.getItem('alp_admin_ai_pending');
        if (pending) { localStorage.removeItem('alp_admin_ai_pending'); send(pending); }
      } catch (e) {}
    }).catch(() => {});
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: msg }, { role: 'assistant', text: '' }]);
    try {
      const token = localStorage.getItem('alp_admin_token');
      await streamChat(`${API}/admin/ai-chat`, {
        sessionId, message: msg, token,
        onDelta: (d) => setMessages((m) => {
          if (!m.length) return m;
          const n = [...m];
          n[n.length - 1] = { ...n[n.length - 1], text: n[n.length - 1].text + d };
          return n;
        }),
        onTool: (t) => setMessages((m) => {
          if (!m.length) return m;
          const n = [...m];
          const tools = [...(n[n.length - 1].tools || [])];
          if (!tools.includes(t)) tools.push(t);
          n[n.length - 1] = { ...n[n.length - 1], tools };
          return n;
        }),
      });
    } catch (e) {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { role: 'assistant', text: `Sorry \u2014 ${e.message}` };
        return n;
      });
    } finally { setBusy(false); }
  };

  const newChat = () => {
    try { localStorage.removeItem('alp_admin_ai_session'); } catch (e) {}
    setSessionId(getOrCreateSession('alp_admin_ai_session'));
    setMessages([]);
  };

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-4xl space-y-6" data-testid="admin-ai-panel">
      <motion.div variants={fadeUp} className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-alp-cream">AI Business Advisor</h2>
          <p className="text-alp-cream/50 text-sm mt-1">Pulls your live orders, stock, money and customers on demand &mdash; and can act for you (mark orders paid, send cart codes) with your confirmation.</p>
        </div>
        <button onClick={newChat} data-testid="admin-ai-new-chat" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase border border-alp-line text-alp-cream/70 hover:border-alp-gold hover:text-alp-gold transition-colors"><RotateCcw size={13} /> New chat</button>
      </motion.div>

      <motion.div variants={fadeUp} className="alp-beam rounded-3xl p-[1.5px]">
        <div className="rounded-[calc(1.5rem-1.5px)] bg-alp-black/95 backdrop-blur-2xl flex flex-col h-[600px] overflow-hidden relative">
          <div className="absolute -top-24 left-1/3 w-72 h-72 rounded-full bg-alp-gold/10 blur-3xl pointer-events-none" />
          <div className="flex items-center gap-2 px-6 pt-5 pb-3">
            <Sparkles size={16} strokeWidth={1.5} className="text-alp-gold animate-pulse" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-alp-gold">Luxe Intelligence</span>
            <span className="text-[9px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-full border border-alp-gold/40 text-alp-gold/90">Claude</span>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {messages.length === 0 && (
              <div className="pt-14 text-center">
                <div className="w-14 h-14 rounded-2xl border border-alp-gold/30 bg-alp-gold/10 flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(212,175,55,0.15)]">
                  <Bot size={24} strokeWidth={1.5} className="text-alp-gold" />
                </div>
                <p className="text-alp-cream/70 text-sm mb-6">Ask me anything about your numbers.</p>
                <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="admin-ai-suggestion"
                      className="px-4 py-2 rounded-full text-xs text-alp-cream/75 border border-alp-line bg-alp-card/40 backdrop-blur-xl hover:border-alp-gold/60 hover:text-alp-gold hover:-translate-y-0.5 transition-[border-color,color,transform] duration-300">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                {m.tools && m.tools.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="admin-ai-tool-chips">
                    {m.tools.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] tracking-wide uppercase border border-alp-gold/30 bg-alp-gold/10 text-alp-gold/90">
                        <Zap size={9} /> {TOOL_LABELS[t] || t}
                      </span>
                    ))}
                  </div>
                )}
                <div data-testid={`admin-ai-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === 'user'
                    ? 'bg-alp-gold text-alp-black shadow-[0_0_20px_rgba(212,175,55,0.25)]'
                    : 'bg-alp-card/50 backdrop-blur-xl border border-alp-gold/20 text-alp-cream'}`}>
                  {m.text || <span className="inline-flex gap-1 items-center text-alp-gold"><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-alp-gold animate-bounce [animation-delay:240ms]" /></span>}
                </div>
              </motion.div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="border-t border-alp-gold/15 bg-alp-card/30 backdrop-blur-xl p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder="e.g. What should I restock this week?"
              data-testid="admin-ai-input"
              className="flex-1 bg-alp-dark/70 border border-alp-line rounded-xl px-4 py-3 text-sm text-alp-cream outline-none focus:border-alp-gold transition-colors"
            />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="admin-ai-send"
              className="px-5 rounded-xl bg-alp-gold text-alp-black hover:bg-alp-goldlight transition-colors disabled:opacity-40 shadow-[0_0_18px_rgba(212,175,55,0.3)]"><Send size={16} /></button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
